import rolePermission from '@/constants/rolePermission';
import { DEFAULT_LAYOUT } from '../base';
import { AppRouteRecordRaw } from '../types';

const Finance: AppRouteRecordRaw = {
  path: '/finance',
  name: 'Finance',
  component: DEFAULT_LAYOUT,
  meta: {
    title: '财务对账',
    requiresAuth: true,
    icon: 'icon-apps',
    order: 5,
  },
  children: [
    {
      path: 'supplier-reconciliation',
      name: 'SupplierReconciliation',
      component: () =>
        import('@/views/finance/supplierReconciliation/index.vue'),
      meta: {
        title: '供应商对账',
        requiresAuth: true,
        roles: ['supplierRecordGet'],
      },
    },
    {
      path: 'supplier-bill',
      name: 'SupplierBill',
      props: (route: any) => ({
        id: route.query.id, // 供应商id
      }),
      component: () =>
        import('@/views/finance/supplierReconciliation/supplierBillList.vue'),
      meta: {
        title: '对账详情',
        requiresAuth: true,
        roles: ['supplierRecordGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'add-payment',
      name: 'AddPayment',
      component: () =>
        import('@/views/finance/supplierReconciliation/addOrEdit.vue'),
      meta: {
        title: '付款/收款',
        requiresAuth: true,
        roles: ['supplierRecordPay', 'supplierInfoGetDataPermission'],
        hideInMenu: true,
      },
    },
    {
      path: 'add-advance',
      name: 'AddAdvance',
      component: () => import('@/views/finance/feeAdvance/addOrEdit.vue'),
      props: (route: any) => ({
        recordId: route.query.recordId, // 对账记录id
        copy: route.query.copy, // 对账记录id
        feeAdvanceOrderId: route.query.feeAdvanceOrderId, // 预支单id
      }),
      meta: {
        title: '新增费用预支',
        requiresAuth: true,
        roles: rolePermission.feeAdvanceOrderEdit,
        permissionMode: 'some',
        hideInMenu: true,
        noScrollStorage: true, // 新增页需要该字段，表示不需要使用记住的滚动位置
      },
    },
    {
      path: 'edit-advance',
      name: 'EditAdvance',
      component: () => import('@/views/finance/feeAdvance/addOrEdit.vue'),
      props: (route: any) => ({
        feeAdvanceOrderId: route.query.feeAdvanceOrderId, // 预支单id
      }),
      meta: {
        title: '编辑费用预支',
        requiresAuth: true,
        roles: rolePermission.feeAdvanceOrderGet,
        permissionMode: 'some',
        hideInMenu: true,
      },
    },
    {
      path: 'advance-detail',
      name: 'AdvanceDetail',
      component: () => import('@/views/finance/feeAdvance/detail.vue'),
      props: (route: any) => ({
        feeAdvanceOrderId: route.query.feeAdvanceOrderId, // 预支单id
      }),
      meta: {
        title: '费用预支详情',
        requiresAuth: true,
        roles: rolePermission.feeAdvanceOrderGet,
        permissionMode: 'some',
        hideInMenu: true,
      },
    },
    {
      path: 'add-expense',
      name: 'AddExpense',
      component: () =>
        import('@/views/finance/feeReimbursement/addExpense.vue'),
      props: (route: any) => ({
        recordId: route.query.recordId, // 对账记录id
        copy: route.query.copy, // 对账记录id
        feeReimbursementOrderId: route.query.feeReimbursementOrderId, // 报销单id
      }),
      meta: {
        title: '新增费用报销',
        requiresAuth: true,
        roles: rolePermission.feeReimbursementOrderEdit,
        permissionMode: 'some',
        hideInMenu: true,
        noScrollStorage: true, // 新增页需要该字段，表示不需要使用记住的滚动位置
      },
    },
    {
      path: 'edit-expense',
      name: 'EditExpense',
      component: () =>
        import('@/views/finance/feeReimbursement/addExpense.vue'),
      props: (route: any) => ({
        feeReimbursementOrderId: route.query.feeReimbursementOrderId, // 报销单id
      }),
      meta: {
        title: '编辑费用报销',
        requiresAuth: true,
        roles: rolePermission.feeReimbursementOrderGet,
        permissionMode: 'some',
        hideInMenu: true,
      },
    },
    {
      path: 'expense-detail',
      name: 'ExpenseDetail',
      component: () =>
        import('@/views/finance/feeReimbursement/expenseDetail.vue'),
      props: (route: any) => ({
        feeReimbursementOrderId: route.query.feeReimbursementOrderId, // 报销单id
      }),
      meta: {
        title: '费用报销详情',
        requiresAuth: true,
        roles: rolePermission.feeReimbursementOrderGet,
        permissionMode: 'some',
        hideInMenu: true,
      },
    },
    {
      path: 'payment-order',
      name: 'PaymentOrder',
      component: () =>
        import('@/views/finance/supplierReconciliation/payDetail.vue'),
      meta: {
        title: '付款单/收款单',
        requiresAuth: true,
        roles: ['supplierRecordGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'account-detail',
      name: 'AccountDetail',
      props: (route: any) => ({
        businessSn: route.query.businessSn,
      }),
      component: () =>
        import('@/views/finance/supplierReconciliation/accountDetail.vue'),
      meta: {
        title: '记账详情',
        requiresAuth: true,
        roles: ['supplierRecordGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'salary-piece-rate',
      name: 'SalaryPieceRate',
      component: () => import('@/views/finance/salaryPieceRate/index.vue'),
      meta: {
        title: '薪资计件',
        requiresAuth: true,
        roles: ['remunerationSettlementGet'],
      },
    },
    {
      path: 'set-salary',
      name: 'SetSalary',
      component: () =>
        import('@/views/finance/salaryPieceRate/addAndEditSalary.vue'),
      meta: {
        title: '薪资设定',
        requiresAuth: true,
        roles: ['remunerationSettlementSet'],
        hideInMenu: true,
      },
    },
    {
      path: 'number-statistical',
      name: 'NumberStatistical',
      component: () =>
        import('@/views/finance/salaryPieceRate/numberStatistical.vue'),
      meta: {
        title: '计件统计',
        requiresAuth: true,
        roles: ['remunerationSettlementGet'],
        hideInMenu: true,
        ignoreCache: true,
      },
    },
    {
      path: 'settlement-record',
      name: 'SettlementRecord',
      component: () =>
        import('@/views/finance/salaryPieceRate/settlementRecord.vue'),
      meta: {
        title: '历史结算记录',
        requiresAuth: true,
        roles: ['remunerationSettlementSubmit'],
        hideInMenu: true,
      },
    },
    {
      path: 'report-detail',
      name: 'ReportDetail',
      component: () =>
        import('@/views/finance/salaryPieceRate/reportDetail.vue'),
      meta: {
        title: '上报明细',
        requiresAuth: true,
        roles: ['*'],
        hideInMenu: true,
        ignoreCache: true,
      },
    },
    {
      path: 'account-flow',
      name: 'AccountFlow',
      component: () => import('@/views/finance/accountFlow/index.vue'),
      meta: {
        title: '账户流水',
        requiresAuth: true,
        roles: ['accountRecordGet'],
      },
    },
    {
      path: 'income-log',
      name: 'IncomeLog',
      component: () => import('@/views/finance/accountFlow/incomeLog.vue'),
      meta: {
        title: '收入记录',
        requiresAuth: true,
        roles: ['accountRecordGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'outcome-log',
      name: 'OutcomeLog',
      component: () => import('@/views/finance/accountFlow/outcomeLog.vue'),
      meta: {
        title: '支出记录',
        requiresAuth: true,
        roles: ['accountRecordGet'],
        hideInMenu: true,
      },
    },
    {
      path: 'fee-advance',
      name: 'FeeAdvance',
      component: () => import('@/views/finance/feeAdvance/index.vue'),
      meta: {
        title: '费用预支',
        requiresAuth: true,
        roles: rolePermission.feeAdvanceOrderGet,
        permissionMode: 'some',
      },
    },
    {
      path: 'fee-reimbursement',
      name: 'FeeReimbursement',
      component: () => import('@/views/finance/feeReimbursement/index.vue'),
      meta: {
        title: '费用报销',
        requiresAuth: true,
        roles: rolePermission.feeReimbursementOrderGet,
        permissionMode: 'some',
      },
    },
  ],
};

export default Finance;
